import React, { useRef, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { CSSTransition } from 'react-transition-group';

import './ContactUs.sass';
import SubmitButton from '../../components/SubmitButton/SubmitButton';
import TextField from '../../components/TextField/TextField';
import formSettings from './formSettings';
import Alert from '../../components/Alert/Alert';
import { useHipaaForm } from '../../hooks/useHipaaForm';

const ContactUs = function ContactUs() {
  const { isSuccess, isSubmitting, error, errors, control, handleSubmit, setValue, setIsSuccess } =
    useHipaaForm('contact-us', formSettings);

  const alertRef = useRef(null);

  // Focus alert when it appears for accessibility
  useEffect(() => {
    if (isSuccess && alertRef.current) {
      alertRef.current.focus();
    }
  }, [isSuccess]);

  return (
    <div className="form-wrap">
      <CSSTransition in={isSuccess} unmountOnExit classNames="alert" timeout={300}>
        <Alert
          ref={alertRef}
          message={'Thank you for contacting us'}
          onClose={() => setIsSuccess(false)}
        />
      </CSSTransition>

      {error && (
        <div className="form-error" role="alert" aria-live="assertive">
          <strong>Error:</strong> {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="contactus-form" noValidate>
        <fieldset disabled={isSubmitting} aria-busy={isSubmitting}>
          <legend className="sr-only">{formSettings.title || 'Contact Us Form'}</legend>

          {formSettings.fields.map((f) => (
            <div key={f.key} className="form-field">
              <Controller
                defaultValue=""
                control={control}
                name={f.key}
                rules={{
                  required: f.isRequired,
                  maxLength: f.maxLength,
                  pattern: f.pattern,
                  validate: {
                    required: (v) => !f.isRequired || !!v.toString().trim()
                  }
                }}
                render={({ field: { onChange, value, ref } }) => (
                  <TextField
                    inputRef={ref}
                    name={f.key}
                    text={f.name}
                    type={f.type}
                    options={f.options}
                    placeholder={f.placeholder}
                    value={setValue(f, value)}
                    handleChange={(val) => onChange(val.value)}
                    aria-required={f.isRequired}
                    aria-invalid={!!errors[f.key]}
                    aria-describedby={errors[f.key] ? `${f.key}-error` : undefined}
                  />
                )}
              />
              {errors[f.key] && (
                <div
                  id={`${f.key}-error`}
                  className="error"
                  role="alert"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {errors[f.key].type === 'required' && `${f.name} is required`}
                  {errors[f.key].type === 'maxLength' && 'Max length exceeded'}
                  {errors[f.key].type === 'pattern' && `${f.name} is invalid`}
                </div>
              )}
            </div>
          ))}

          <div>
            <SubmitButton
              text={formSettings.submitBtn.text}
              disabled={isSubmitting}
              aria-disabled={isSubmitting}
            />
          </div>
        </fieldset>
      </form>
    </div>
  );
};

export default ContactUs;
